import { z } from 'zod'

export const MAX_AVAILABILITY_RANGE_DAYS = 62
export const CONSULTATION_PROJECT_NAME_MAX_LENGTH = 200
export const CONSULTATION_ORGANIZATION_NAME_MAX_LENGTH = 200
export const CONSULTATION_FULL_NAME_MAX_LENGTH = 200
export const CONSULTATION_WEBSITE_MAX_LENGTH = 2048
export const CONSULTATION_CONTEXT_MAX_LENGTH = 4000
export const CONSULTATION_WEEKDAY_KEYS: readonly [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export function daysBetweenInclusive(from: string, to: string): number {
  const fromUtc = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)))
  const toUtc = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)))
  return Math.floor((toUtc - fromUtc) / 86_400_000) + 1
}

export const consultationIsoDateSchema = z.string().date()

export function isMinuteAlignedUtc(value: string | Date): boolean {
  const time = typeof value === 'string' ? Date.parse(value) : value.getTime()
  return Number.isFinite(time) && time % 60_000 === 0
}

export const consultationIsoDateTimeSchema = z
  .string()
  .datetime({ offset: true })
  .refine(isMinuteAlignedUtc, 'Date-time must be minute-aligned')
export const consultationTimeSchema = z
  .string()
  .time()
  .refine((value) => value.length === 5, 'Time must use HH:mm precision')

export function consultationTimeToMinutes(value: string): number | null {
  if (!consultationTimeSchema.safeParse(value).success) {
    return null
  }

  const hour = Number(value.slice(0, 2))
  const minute = Number(value.slice(3, 5))
  return hour * 60 + minute
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
    return true
  } catch {
    return false
  }
}

export const consultationAvailabilitySlotSchema = z
  .object({
    start: consultationIsoDateTimeSchema,
    end: consultationIsoDateTimeSchema,
  })
  .superRefine((value, context) => {
    if (Date.parse(value.start) >= Date.parse(value.end)) {
      context.addIssue({
        code: 'custom',
        message: 'Slot end must be after start',
        path: ['end'],
      })
    }
  })

export const consultationAvailabilityPolicySchema = z.object({
  minAllowed: consultationIsoDateSchema,
  maxAllowed: consultationIsoDateSchema,
  meetingDurationMinutes: z.number().int().positive(),
  timeZone: z.string().refine(isValidIanaTimeZone, 'Select a valid time zone'),
})

export const consultationAvailabilityResponseSchema = z
  .object({
    ok: z.literal(true),
    from: consultationIsoDateSchema,
    to: consultationIsoDateSchema,
    enabledDates: z.array(consultationIsoDateSchema),
    slotsByDate: z.record(z.string(), z.array(consultationAvailabilitySlotSchema)),
    policy: consultationAvailabilityPolicySchema,
  })
  .superRefine((value, context) => {
    for (const dateKey of Object.keys(value.slotsByDate)) {
      if (!consultationIsoDateSchema.safeParse(dateKey).success) {
        context.addIssue({
          code: 'custom',
          message: 'slotsByDate keys must be valid YYYY-MM-DD dates',
          path: ['slotsByDate', dateKey],
        })
      }
    }

    for (const enabledDate of value.enabledDates) {
      const slots = value.slotsByDate[enabledDate]
      if (!slots || slots.length === 0) {
        context.addIssue({
          code: 'custom',
          message: 'enabledDates must have matching non-empty slotsByDate entries',
          path: ['enabledDates'],
        })
      }
    }
  })

export const consultationAvailabilityRangeQuerySchema = z
  .object({
    from: consultationIsoDateSchema,
    to: consultationIsoDateSchema,
  })
  .superRefine((value, context) => {
    if (value.from > value.to) {
      context.addIssue({
        code: 'custom',
        message: 'from must be less than or equal to to',
        path: ['from'],
      })
    }

    const dayCount = daysBetweenInclusive(value.from, value.to)
    if (dayCount > MAX_AVAILABILITY_RANGE_DAYS) {
      context.addIssue({
        code: 'custom',
        message: `Date range must be at most ${MAX_AVAILABILITY_RANGE_DAYS} days`,
        path: ['to'],
      })
    }
  })

export const CONSULTATION_TYPE_VALUES = ['project', 'market-maker'] as const
export const CONSULTATION_TOKEN_STAGE_VALUES = ['pre-tge', 'post-tge'] as const
export const CONSULTATION_TGE_TIMELINE_VALUES = [
  'unknown',
  'imminent',
  '1-month',
  '3-months',
  '6-months-or-more',
] as const
export const CONSULTATION_SUPPORT_AREA_VALUES = [
  'tokenomics',
  'market-maker-engagement',
  'exchange-listing',
  'market-maker-monitoring',
  'other',
] as const
export const CONSULTATION_FUNDING_RANGE_VALUES = ['no', '0-1m', '1m-2.5m', '2.5m-5m', '5m-10m', 'over-10m'] as const
export const CONSULTATION_ENGAGEMENT_STRUCTURE_VALUES = ['loan_call_option', 'retainer_working_capital'] as const
export const CONSULTATION_ACTIVE_ENGAGEMENT_RANGE_VALUES = ['0', '1-5', '6-10', '11-20', '21-plus'] as const
export const CONSULTATION_FIRM_EXPERTISE_VALUES = ['cex', 'dex', 'cex-and-dex', 'other'] as const

export const consultationBookScheduleSchema = z.object({
  date: consultationIsoDateSchema.describe('Date key from availability slotsByDate in the booking policy timezone'),
  slot: consultationAvailabilitySlotSchema,
  timezone: z
    .string()
    .refine(isValidIanaTimeZone, 'Select a valid time zone')
    .describe('Visitor IANA timezone used for consultation context'),
})

export const consultationBookProjectSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Project name is required')
      .max(CONSULTATION_PROJECT_NAME_MAX_LENGTH, 'Project name is too long'),
    website: z
      .string()
      .trim()
      .url('Enter a valid website URL')
      .max(CONSULTATION_WEBSITE_MAX_LENGTH, 'Website URL is too long'),
    tokenStage: z.enum(CONSULTATION_TOKEN_STAGE_VALUES, { message: 'Select a TGE status' }),
    tgeTimeline: z.enum(CONSULTATION_TGE_TIMELINE_VALUES).nullable(),
    supportAreas: z.array(z.enum(CONSULTATION_SUPPORT_AREA_VALUES)).min(1, 'Select at least one area of support'),
    fundingRange: z.enum(CONSULTATION_FUNDING_RANGE_VALUES, { message: 'Select a funding range' }),
  })
  .superRefine((value, context) => {
    if (value.tokenStage === 'pre-tge' && !value.tgeTimeline) {
      context.addIssue({
        code: 'custom',
        message: 'Select a TGE timeline',
        path: ['tgeTimeline'],
      })
    }
  })

export const consultationBookMarketMakerSchema = z.object({
  organizationName: z
    .string()
    .trim()
    .min(1, 'Market Maker name is required')
    .max(CONSULTATION_ORGANIZATION_NAME_MAX_LENGTH, 'Market Maker name is too long'),
  engagementStructures: z
    .array(z.enum(CONSULTATION_ENGAGEMENT_STRUCTURE_VALUES))
    .min(1, 'Select at least one engagement structure'),
  activeEngagementRange: z.enum(CONSULTATION_ACTIVE_ENGAGEMENT_RANGE_VALUES, {
    message: 'Select an active engagements range',
  }),
  worksWithCompetitor: z.boolean({ message: 'Select Yes or No' }),
  dataSharingConsent: z.boolean({ message: 'Select Yes or No' }),
  firmExpertise: z.enum(CONSULTATION_FIRM_EXPERTISE_VALUES, { message: 'Select firm expertise' }),
})

export const consultationBookContactBaseSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, 'Full name is required')
    .max(CONSULTATION_FULL_NAME_MAX_LENGTH, 'Full name is too long'),
  email: z.string().trim().email('Enter a valid email address'),
  privacyAccepted: z.literal(true, {
    errorMap: () => ({ message: 'Accept the Privacy Policy to continue' }),
  }),
})

export const consultationBookProjectContactSchema = consultationBookContactBaseSchema.extend({
  context: z.string().max(CONSULTATION_CONTEXT_MAX_LENGTH, 'Context is too long'),
})

export const consultationBookMarketMakerContactSchema = consultationBookContactBaseSchema.extend({
  website: z
    .string()
    .trim()
    .url('Enter a valid website URL')
    .max(CONSULTATION_WEBSITE_MAX_LENGTH, 'Website URL is too long'),
})

const consultationBookRequestBaseSchema = z.object({
  schedule: consultationBookScheduleSchema,
  turnstileToken: z.string().optional(),
})

export const consultationBookProjectRequestSchema = consultationBookRequestBaseSchema.extend({
  consultationType: z.literal('project'),
  project: consultationBookProjectSchema,
  contact: consultationBookProjectContactSchema,
})

export const consultationBookMarketMakerRequestSchema = consultationBookRequestBaseSchema.extend({
  consultationType: z.literal('market-maker'),
  marketMaker: consultationBookMarketMakerSchema,
  contact: consultationBookMarketMakerContactSchema,
})

export const consultationBookRequestSchema = z.discriminatedUnion('consultationType', [
  consultationBookProjectRequestSchema,
  consultationBookMarketMakerRequestSchema,
])

const consultationBookResponseBaseSchema = z.object({
  ok: z.literal(true),
  start: consultationIsoDateTimeSchema,
  end: consultationIsoDateTimeSchema,
  summary: z.string().min(1),
})

export const consultationBookCalendarResponseSchema = consultationBookResponseBaseSchema.extend({
  bookingMode: z.literal('calendar'),
  eventId: z.string().min(1),
  meetLink: z.string().nullable(),
  calendarEventLink: z.string().nullable(),
})

export const consultationBookManualResponseSchema = consultationBookResponseBaseSchema.extend({
  bookingMode: z.literal('manual'),
  eventId: z.null(),
  meetLink: z.null(),
  calendarEventLink: z.null(),
})

export const consultationBookResponseSchema = z.discriminatedUnion('bookingMode', [
  consultationBookCalendarResponseSchema,
  consultationBookManualResponseSchema,
])

export const consultationBookConflictSchema = z.object({
  ok: z.literal(false),
  code: z.literal('SLOT_ALREADY_BOOKED'),
  message: z.string().min(1),
})

export const consultationApiMessageErrorSchema = z.object({
  message: z.string(),
})

export type ConsultationType = (typeof CONSULTATION_TYPE_VALUES)[number]
export type ConsultationAvailabilitySlot = z.infer<typeof consultationAvailabilitySlotSchema>
export type ConsultationAvailabilityResponse = z.infer<typeof consultationAvailabilityResponseSchema>
export type ConsultationAvailabilityRangeQuery = z.infer<typeof consultationAvailabilityRangeQuerySchema>
export type ConsultationSlotsByDate = ConsultationAvailabilityResponse['slotsByDate']
export type ConsultationBookProject = z.infer<typeof consultationBookProjectSchema>
export type ConsultationBookMarketMaker = z.infer<typeof consultationBookMarketMakerSchema>
export type ConsultationBookProjectContact = z.infer<typeof consultationBookProjectContactSchema>
export type ConsultationBookMarketMakerContact = z.infer<typeof consultationBookMarketMakerContactSchema>
export type ConsultationBookProjectRequest = z.infer<typeof consultationBookProjectRequestSchema>
export type ConsultationBookMarketMakerRequest = z.infer<typeof consultationBookMarketMakerRequestSchema>
export type ConsultationBookRequest = z.infer<typeof consultationBookRequestSchema>
export type ConsultationBookCalendarResponse = z.infer<typeof consultationBookCalendarResponseSchema>
export type ConsultationBookManualResponse = z.infer<typeof consultationBookManualResponseSchema>
export type ConsultationBookResponse = z.infer<typeof consultationBookResponseSchema>
export type ConsultationBookConflict = z.infer<typeof consultationBookConflictSchema>
export type ConsultationApiMessageError = z.infer<typeof consultationApiMessageErrorSchema>
